<template>
  <div
    ref="element"
    class="item"
    :style="styleTranslateEffect"
    :class="{ 'intro-complete': introComplete }"
    @mouseover="handleMouseOver"
    @mouseout="handleMouseOut"
  >
    <div
      ref="graphicElement"
      :style="styleRotationEffect"
      class="graphics-wrapper"
    >
      <DialogTrophiesIconsItemGraphic class="front" />
      <img
        :src="medias.get(trophy.icon).urlCompressed"
        :alt="trophy.shortTitle"
        class="back"
      />
    </div>
    <div ref="textsElement" class="texts">
      <span class="name">{{ trophy.shortTitle }}</span>
    </div>
  </div>
</template>
<script setup lang="ts">
// @ts-ignore
import { spring } from 'vue3-spring';
import { gsap } from 'gsap';

interface Props {
  trophy: TrophySchema;
  medias: Map<string, MediaSchema>;
  ready: boolean;
  index: number;
}

const props = defineProps({
  trophy: null,
  medias: null,
  ready: { type: Boolean },
  index: null
});

const emit = defineEmits(["select", "updatePosition"]);

const { start: startSelectTimer, stop: stopSelectTimer } = useTimeoutFn(
  () => {
    emit('select', props.index);
  },
  200,
  { immediate: false }
);

const handleMouseOver = () => {
  startSelectTimer();
};

const handleMouseOut = () => {
  stopSelectTimer();
};

const element = ref();

const shadowElement = ref();

const introComplete = ref<boolean>(false);

const { x, width } = useElementBounding(shadowElement);

watch(
  () => introComplete.value,
  (b) => {
    if (b) shadowElement.value = element.value;
  }
);

const graphicElement = ref();

const textsElement = ref();

const translate = ref<number>(35);

const translateEffect = spring(translate, {
  damping: 12 + props.index * 2,
  stiffness: 120 + props.index * 2,
  mass: 1,
  from: translate.value,
});

const styleTranslateEffect = computed(() => {
  return {
    transform: `translateX(${translateEffect.value}rem)`,
    opacity: 1,
  };
});

const rotation = ref<number>(0);

const readyForReveal = ref<boolean>(false);

const rotationEffect = spring(rotation, {
  damping: 6,
  stiffness: 50,
  mass: 1,
  from: 0,
});

const styleRotationEffect = computed(() => {
  return {
    transform: `rotateY(${rotationEffect.value}deg)`,
  };
});

const { start: startReleal } = useTimeoutFn(
  () => {
    rotation.value = 180;
    playAnimationText();
  },
  400,
  { immediate: false }
);

const initAnimation = () => {
  const tl = gsap.timeline({ delay: 0 });
};

const _completeAnimationText = () => {
  introComplete.value = true;
};

const playAnimationText = () => {
  const tl = gsap.timeline({ delay: 0 });
  tl.fromTo(
    textsElement.value,
    {
      opacity: 0,
      scale: 1.3,
    },
    {
      duration: 0.8,
      opacity: 1,
      scale: 1,
      ease: `quad.out`,
      delay: Math.sin(props.index * 0.1),
      onComplete: _completeAnimationText,
    },
    0
  );
};

watch(
  () => props.ready,
  (b) => {
    if (b) {
      translate.value = 0;
    }
  }
);

watch(
  () => translateEffect.value,
  (val) => {
    if (val <= 0) {
      readyForReveal.value = true;
    }
  }
);

watch(
  () => readyForReveal.value,
  (b) => {
    if (b) startReleal();
  }
);

watchEffect(() => {
  emit('updatePosition', props.index, x.value + width.value * 0.5);
});

onMounted(() => {
  initAnimation();
});
</script>
<style lang="scss" scoped>
.graphics-wrapper {
  position: relative;
  width: 9rem;
  height: 9rem;
  transform-style: preserve-3d;
  backface-visibility: hidden;
  perspective: 1200px;
  perspective-origin: center;
}

.texts {
  overflow: hidden;
  opacity: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.name {
  display: block;
  @include font('s', 400, 'secondary');

  color: var(--color_neutral_25);
}

.back,
.front {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.front {
  position: relative;
  z-index: 1;

  // transform: translateZ(1px);
  backface-visibility: hidden;
}

.back {
  transform: rotateY(180deg);
  backface-visibility: visible;
}

.item {
  display: flex;
  flex: 0 0 9rem;
  flex-direction: column;
  width: 9rem;
  overflow: hidden;
  gap: var(--gap_l);
}
</style>
